import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

import { useStationPage } from "../components/StationLayout";
import { fetchJson, NotFoundError } from "../lib/api";
import {
  type Episode,
  type EpisodesPageData,
  formatEpisodeDate,
  showEpisodesPath,
} from "../lib/publicPages";

type ArchiveState =
  | { kind: "loading" }
  | { kind: "missing" }
  | { kind: "error" }
  | { kind: "ready"; episodes: Episode[]; nextCursor: string | null };

/** The cursor param out of the envelope's `next` URL, or null at the end. */
const cursorOf = (data: EpisodesPageData): string | null => {
  if (!data.next) {
    return null;
  }
  try {
    return new URL(data.next, window.location.origin).searchParams.get(
      "cursor",
    );
  } catch {
    return null;
  }
};

/**
 * A show's full episode archive, newest first, below the station's shared
 * header (see StationLayout). Pages through the cursor feed the apps use;
 * the show page links here and carries only the head of this list itself.
 */
const EpisodesPage = () => {
  const { stationSlug, showSlug } = useParams({
    from: "/$stationSlug/shows/$showSlug/episodes",
  });
  const { status, directory } = useStationPage();
  const [state, setState] = useState<ArchiveState>({ kind: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);
  // After a failed page fetch, auto-loading stands down until a real
  // click — otherwise a persistent error would retry in a loop for as
  // long as the button stayed in view.
  const [autoPaused, setAutoPaused] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();
    setState({ kind: "loading" });
    fetchJson<EpisodesPageData>(
      showEpisodesPath(stationSlug, showSlug),
      controller.signal,
    )
      .then((data) => {
        if (!disposed) {
          setState({
            kind: "ready",
            episodes: data.results,
            nextCursor: cursorOf(data),
          });
        }
      })
      .catch((error: Error) => {
        if (disposed || error?.name === "AbortError") {
          return;
        }
        setState(
          error instanceof NotFoundError
            ? { kind: "missing" }
            : { kind: "error" },
        );
      });
    return () => {
      disposed = true;
      controller.abort();
    };
  }, [stationSlug, showSlug]);

  const loadMore = useCallback(
    (episodes: Episode[], cursor: string) => {
      setLoadingMore(true);
      fetchJson<EpisodesPageData>(
        showEpisodesPath(stationSlug, showSlug, cursor),
      )
        .then((data) => {
          setState({
            kind: "ready",
            episodes: [...episodes, ...data.results],
            nextCursor: cursorOf(data),
          });
        })
        .catch(() => {
          // The page keeps what it has; the button stays for another try.
          setAutoPaused(true);
        })
        .finally(() => setLoadingMore(false));
    },
    [stationSlug, showSlug],
  );

  // Reaching the bottom presses "More episodes" by itself: the observer
  // fires as the button scrolls into reach, and the button remains the
  // fallback (and the retry) whenever it can't.
  useEffect(() => {
    if (state.kind !== "ready" || loadingMore || autoPaused) {
      return;
    }
    const { episodes, nextCursor } = state;
    const button = moreButtonRef.current;
    if (!nextCursor || !button) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          loadMore(episodes, nextCursor);
        }
      },
      { rootMargin: "400px" },
    );
    observer.observe(button);
    return () => observer.disconnect();
  }, [state, loadingMore, autoPaused, loadMore]);

  if (state.kind === "loading") {
    return <p className="station__quiet mono">Loading…</p>;
  }
  if (state.kind !== "ready") {
    return (
      <div className="station__detail">
        <h2 className="station__detail-name">
          {state.kind === "missing"
            ? "No such show."
            : "Couldn't load these episodes."}
        </h2>
        <p className="station__blurb">
          {state.kind === "missing"
            ? "This station has no show at this address — it may have been renamed."
            : "Try again in a moment."}
        </p>
        <Link className="btn" to="/$stationSlug/shows" params={{ stationSlug }}>
          All shows
        </Link>
      </div>
    );
  }

  // Slugless shows aren't in the directory; the raw segment stands in.
  const showName =
    directory?.shows.find((show) => show.slug === showSlug)?.name ?? showSlug;

  return (
    <div className="station__detail">
      <nav className="station__crumbs mono" aria-label="Breadcrumb">
        <Link to="/$stationSlug" params={{ stationSlug }}>
          {status.name}
        </Link>
        <span aria-hidden> / </span>
        <Link
          to="/$stationSlug/shows/$showSlug"
          params={{ stationSlug, showSlug }}
        >
          {showName}
        </Link>
      </nav>

      <h2 className="station__detail-name">Episodes</h2>

      {state.episodes.length === 0 ? (
        <p className="station__blurb">No episodes here yet.</p>
      ) : (
        <div className="station__recent">
          <ul className="station__episodes">
            {state.episodes.map((episode) => (
              <li key={episode.id}>
                <Link
                  to="/$stationSlug/shows/$showSlug/episodes/$episodeId"
                  params={{ stationSlug, showSlug, episodeId: episode.id }}
                >
                  <span className="station__episode-title">
                    {episode.title}
                  </span>
                  <span className="station__when mono">
                    {formatEpisodeDate(episode.start_date)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {state.nextCursor && (
        <button
          ref={moreButtonRef}
          className="btn"
          type="button"
          disabled={loadingMore}
          onClick={() => {
            setAutoPaused(false);
            loadMore(state.episodes, state.nextCursor as string);
          }}
        >
          {loadingMore ? "Loading…" : "More episodes"}
        </button>
      )}
    </div>
  );
};

export default EpisodesPage;
